import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Button,
  Box,
} from '@mui/material';
import { SubscriptionTier } from '@/../generated-client/models';
import {
  requestUpdateSubscriptionViaSales,
  updateSubscriptionViaSales,
  updateOrganizationViaSales,
} from '../../../../repository/OrganizationDAO';
import { useUserContext } from '../../../../context_providers/user_context/UserProvider';
import LoadingData from '../../../../components/loading/LoadingData';
import ImpactedUsersModalSales from '../../../../components/modals/utils/ImpactedUsersModalSales';
import { convertTimestampToDateInputValue } from '../../../../utils/date_convert';
import { useToast } from '../../../../context_providers/toast_context/ToastContext';
import { ToastType } from '../../../../context_providers/toast_context/ToastContext';
import COLORS from '../../../../resources/style/Themes';
import { handleEnterKeyPress } from '../../../../utils/app_support/KeyPressHandler';
import { isThereAnActiveTrial } from '../../../../utils/organization_details_helper/organization_details_helper';
import ConfirmFreeTrialModal from '../../manage_organizations/modals/ConfirmFreeTrialModal';
import { getTierLabelKey } from '@/utils/entitlements/tierLabels';
import { StringKeys } from '@/resources/strings/StringKeys';

function EditOrganizationViaSalesModal({ organization, show, onHide }) {
  const { bearerToken } = useUserContext();
  const { t } = useTranslation();
  const [previewResponse, setPreviewResponse] = useState(null);
  const [showImpactedUsersModal, setShowImpactedUsersModal] = useState(false);
  const [loadingAPIData, setLoadingAPIData] = useState(false);
  const { showToast } = useToast();
  const [isSafeguardOrganization, setIsSafeguardOrganization] = useState(false);
  const [subscriptionStartTimestamp, setSubscriptionStartTimestamp] =
    useState();
  const [trialEnabled, setTrialEnabled] = useState(false);
  const [subscriptionDuration, setSubscriptionDuration] = useState(0);
  const [opsCount, setOpsCount] = useState(0);
  const [respondCount, setRespondCount] = useState(0);
  const [trialEndTimestamp, setTrialEndTimestamp] = useState(0);
  const [isConfirmationDialogOpen, setConfirmationDialogOpen] = useState(false);

  useEffect(() => {
    if (organization) {
      setIsSafeguardOrganization(organization.is_safeguard_organization);
      setSubscriptionStartTimestamp(organization.subscription_start_timestamp);
      setTrialEnabled(isThereAnActiveTrial(organization));
      const targetDate = new Date('2100-01-01T00:00:00Z');
      const currentDate = new Date();
      const differenceInMilliseconds =
        targetDate.getTime() - currentDate.getTime();
      const differenceInDays = Math.ceil(
        differenceInMilliseconds / (24 * 60 * 60 * 1000),
      );
      setSubscriptionDuration(differenceInDays);

      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 90);

      const trialEndDateValue = organization.trial_end_timestamp
        ? convertTimestampToDateInputValue(organization.trial_end_timestamp)
        : convertTimestampToDateInputValue(thirtyDaysFromNow.getTime());
      setTrialEndTimestamp(trialEndDateValue);
      setOpsCount(organization.ops_subscription_count);
      setRespondCount(organization.respond_subscription_count);
      setPreviewResponse(null);
      setShowImpactedUsersModal(false);
    }
  }, [organization, show]);

  const editOrganization = async () => {
    setLoadingAPIData(true);

    const opsOrRespondCountChanged =
      opsCount !== organization.ops_subscription_count ||
      respondCount !== organization.respond_subscription_count;

    if (opsOrRespondCountChanged) {
      try {
        const response = await requestUpdateSubscriptionViaSales(
          organization.org_code,
          organization.id,
          organization.subscription_count,
          opsCount,
          respondCount,
        );
        const totalAffectedUsers =
          response.ops_affected_users.length +
          response.respond_affected_users.length;
        if (totalAffectedUsers > 0) {
          setPreviewResponse(response);
          setShowImpactedUsersModal(true);
          setLoadingAPIData(false);
          return;
        }
      } catch {
        // Reducing a pool below its current usage no longer blocks (Task 25
        // reverted): the sales endpoints auto-demote the excess users to
        // Free instead of throwing OPS_SEATS_FILLED/RESPOND_SEATS_FILLED, so
        // any error here is unexpected — surface it rather than swallowing it.
        showToast(
          `Error previewing update for ${organization.company_name}.`,
          ToastType.DANGER,
        );
        setLoadingAPIData(false);
        return;
      }
    }

    await proceedWithUpdate();
  };

  const proceedWithUpdate = async () => {
    const trialLength = trialEnabled
      ? new Date(trialEndTimestamp).getTime()
      : null;
    const subscriptionStartDateObj = new Date(subscriptionStartTimestamp);
    const subscriptionStartDateTimestamp = subscriptionStartDateObj.getTime();

    try {
      await updateSubscriptionViaSales(
        organization.org_code,
        organization.id,
        organization.subscription_count,
        opsCount,
        respondCount,
      );

      await updateOrganizationViaSales({
        org_code: organization.org_code,
        features: organization.features,
        is_safeguard_organization: isSafeguardOrganization,
        subscription_start_timestamp: subscriptionStartDateTimestamp,
        trial_end_timestamp: trialLength,
        subscription_duration: subscriptionDuration,
        organization_azure_id: organization.id,
      });
      showToast(
        `Successfully updated ${organization.company_name}.`,
        ToastType.SUCCESS,
      );
      onHide();
    } catch {
      showToast(
        `Error updating ${organization.company_name}.`,
        ToastType.DANGER,
      );
    } finally {
      setLoadingAPIData(false);
    }
  };

  const handleEnterPress = handleEnterKeyPress(editOrganization);

  const handleTrialChange = (e) => {
    setTrialEnabled(e.target.value === 'Enabled');
    if (e.target.value === 'Enabled') {
      setConfirmationDialogOpen(true);
    }
  };

  const handleConfirmationClose = () => {
    setTrialEnabled(false);
    setConfirmationDialogOpen(false);
  };

  const handleConfirmationAgree = () => {
    setTrialEnabled(true);
    setConfirmationDialogOpen(false);
  };

  return (
    <>
      <Dialog
        open={show}
        onClose={onHide}
        fullWidth
        maxWidth="md"
        aria-labelledby="edit-organization-dialog-title"
      >
        <DialogTitle
          sx={{
            backgroundColor: COLORS.SAFEGUARD_BLUE,
            color: COLORS.WHITE,
          }}
        >
          Edit {organization.company_name} Subscription
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" onKeyDown={handleEnterPress} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={6}>
                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel>Enable Trial</InputLabel>
                  <Select
                    value={trialEnabled ? 'Enabled' : 'Disabled'}
                    onChange={handleTrialChange}
                    label="Enable Trial"
                  >
                    <MenuItem value="Enabled">Enabled</MenuItem>
                    <MenuItem value="Disabled">Disabled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={6}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  disabled={!trialEnabled}
                >
                  <TextField
                    type="date"
                    label="Trial End Date"
                    value={trialEndTimestamp}
                    onChange={(e) => setTrialEndTimestamp(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      backgroundColor: trialEnabled
                        ? 'inherit'
                        : 'action.disabledBackground',
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid size={6}>
                <FormControl fullWidth variant="outlined" margin="normal">
                  <TextField
                    label={`${getTierLabelKey(SubscriptionTier.Ops)} ${t(StringKeys.SUBSCRIPTION_COUNT)}`}
                    type="number"
                    value={opsCount}
                    onChange={(e) => setOpsCount(Number(e.target.value))}
                    inputProps={{ min: 0 }}
                  />
                </FormControl>
              </Grid>
              <Grid size={6}>
                <FormControl fullWidth variant="outlined" margin="normal">
                  <TextField
                    label={`${getTierLabelKey(SubscriptionTier.Respond)} ${t(StringKeys.SUBSCRIPTION_COUNT)}`}
                    type="number"
                    value={respondCount}
                    onChange={(e) => setRespondCount(Number(e.target.value))}
                    inputProps={{ min: 0 }}
                  />
                </FormControl>
              </Grid>
              <Grid size={6}>
                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel>Safeguard Organization</InputLabel>
                  <Select
                    value={isSafeguardOrganization ? 'Yes' : 'No'}
                    onChange={(e) =>
                      setIsSafeguardOrganization(e.target.value === 'Yes')
                    }
                    label="Safeguard Organization"
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={editOrganization}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <ImpactedUsersModalSales
        show={showImpactedUsersModal}
        onHide={() => setShowImpactedUsersModal(false)}
        previewResponse={previewResponse}
        organization={organization}
        proceedWithUpdate={proceedWithUpdate}
      />
      <LoadingData show={loadingAPIData} />
      <ConfirmFreeTrialModal
        open={isConfirmationDialogOpen}
        onClose={handleConfirmationClose}
        onConfirm={handleConfirmationAgree}
      />
    </>
  );
}

export default EditOrganizationViaSalesModal;
